import React, {memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, InputAdornment, TextField, debounce} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {useStyles} from "../../components/disconnection-management/styles";
import {getCompanies} from "../../../services/bff-service";
import {Company, CompanyState} from "../../../stores/company/company-state";

interface CompanySearchPropsBase {
    currentTab: string;
    initialValue?: string;
    getSearchResults: (filteredData: any) => void;
}

interface CompanySearchOnline extends CompanySearchPropsBase{
    mode: 'online';
    minCharsToFire: number;
    debounceTime: number;
}

interface CompanySearchOffline extends CompanySearchPropsBase {
    mode: 'offline';
    offlineSearchData: any[];
}

type CompanySearchProps = CompanySearchOnline | CompanySearchOffline;

const CompanySearch  = (props: CompanySearchProps): ReactElement => {
    const styles = useStyles();

    const data = useMemo(() => {
        if (props.mode === "offline"){
            return  props?.offlineSearchData;
        }
        return [];
    }, [props]);

    const searchMode = props.mode;
    const getSearchResults = props.getSearchResults;
    const debounceTime = props.mode === "online" ? props.debounceTime : 0;

    const [searchText, setSearchText] = useState(props?.initialValue ?? '');


    const debouncedGetCompanies = useMemo(
        () =>
            debounce((options) => getCompanies(options)
                    .then(data => getSearchResults(data.results))
                    .catch(console.log)
                , debounceTime),
        [searchMode]
    );

    useEffect(() => {
        // Reset the search field on tab switch or changing the searchSetting.
        setSearchText("");
    }, [props.currentTab]);

    useEffect(() => {
        const abortControllerInstance = (new AbortController());

        if (searchMode === 'offline') {
            const lowerCaseSearchText = searchText.toLowerCase();
            const filteredRows = data?.filter((row) => {
                const matchesSearchText = !searchText
                    ? true
                    : row?.name?.toLowerCase().includes(lowerCaseSearchText) ||
                    row?.codatCompanyId?.includes(lowerCaseSearchText) ||
                    row?.cif?.includes(searchText) ||
                    row?.teamCode?.toLowerCase().includes(lowerCaseSearchText) ||
                    row?.crmEntityId?.includes(searchText);
                return matchesSearchText;
            });

            getSearchResults(filteredRows);
        }
        if (searchMode === "online") {
            const  searchTextLength = searchText.length;
            if (searchTextLength === 0) getSearchResults([]);
            if (searchTextLength >= props.minCharsToFire) {
                debouncedGetCompanies({signal: abortControllerInstance.signal, query: searchText});
            }
        }
        return () => {
            abortControllerInstance.abort("Cancel the long lived in-progress HTTP requests.");
        }
    }, [searchText, searchMode, props])

    const handleChange = ({target: {name, value}}) => {
        switch (name) {
            case 'searchText':
                setSearchText(value)
                break;
        }
    };

    return <Box>
        <TextField
            value={searchText}
            variant="outlined"
            data-testid="search-input"
            name={'searchText'}
            onChange={handleChange}
            className={'searchTextWrapper'}
            placeholder="Search by Company Legal Name, CIF, CRM ID, Team Code, or Codat ID"
            {...(searchMode ==='online' &&  {helperText : `Enter ${props?.minCharsToFire} or more characters to return results.`})}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon className={styles.searchIcon}/>
                    </InputAdornment>
                ),
                classes: {
                    input: styles.searchTypography,
                },
            }}
        />
    </Box>
}

export default memo(CompanySearch);